import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, EmptyState, PageHeader } from "../../components/v3";
import { SubcontractorReportForm, SubcontractorReportResults } from "../../components/report";
import { AnalyticsService } from "../../services";
import { useHasPermission } from "../../utils";
import { GENERATE_SUBCONTRACTOR_REPORT } from "../../constants";
import type { ReportType, SubcontractorReportQuery, SubcontractorReportResponse } from "../../services/analytics.types";

interface PaginationState {
  page: number;
  pageSize: number;
}

const INITIAL_PAGINATION: PaginationState = { page: 0, pageSize: 10 };

export const SubcontractorReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState<SubcontractorReportQuery | null>(null);
  const [reportData, setReportData] = useState<SubcontractorReportResponse | null>(null);
  const [activeTab, setActiveTab] = useState<ReportType>("summary");
  const [paginationState, setPaginationState] = useState<PaginationState>(INITIAL_PAGINATION);

  const hasPermission = useHasPermission(GENERATE_SUBCONTRACTOR_REPORT);

  useEffect(() => {
    setReportData(null);
  }, [activeTab]);

  const fetchData = async (filters: SubcontractorReportQuery, { onError }: { onError?: () => void } = {}) => {
    try {
      const { data } = await AnalyticsService.getSubcontractorReport(filters);
      setReportData(data);
    } catch (error) {
      onError?.();
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to generate report");
      console.error("Error fetching subcontractor report data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = (filters: SubcontractorReportQuery, { onError }: { onError?: () => void } = {}) => {
    setLoading(true);
    setReportData(null);
    setReportFilters(filters);
    setPaginationState(INITIAL_PAGINATION);
    fetchData({ ...filters, page: 1, limit: INITIAL_PAGINATION.pageSize }, { onError });
  };

  const handlePaginationChange = (model: PaginationState) => {
    if (!reportFilters) return;
    setPaginationState(model);
    fetchData({ ...reportFilters, page: model.page + 1, limit: model.pageSize });
  };

  const reportTypeChips = [
    { label: "Summary", onClick: () => setActiveTab("summary") },
    { label: "Details", onClick: () => setActiveTab("detailed") },
  ];

  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader
          title="Subcontractor Report"
          chipItems={reportTypeChips}
          activeTab={activeTab === "summary" ? "summary" : "details"}
        />
      </Box>

      {/* form section */}
      <Box sx={{ mt: -7, zIndex: 2, position: "relative" }}>
        <SubcontractorReportForm reportType={activeTab} handleClear={() => setReportData(null)} onGenerate={handleGenerate} />
      </Box>

      {/* results section */}
      {!loading &&
        reportData &&
        reportFilters &&
        (reportData.count > 0 ? (
          <SubcontractorReportResults
            reportType={activeTab}
            data={reportData}
            loading={loading}
            paginationState={paginationState}
            handlePaginationChange={handlePaginationChange}
            reportFilters={reportFilters}
            showExportButton={hasPermission}
          />
        ) : (
          <CardLayout>
            <EmptyState title="No Data Found" subtitle="No report found for selected filters!" />
          </CardLayout>
        ))}
    </PageLayout>
  );
};
